// ===================================
// VIEW PRESETS REPOSITORY
// Maneja presets de vistas compartidas y del sistema
// Path: Companies/{companyId}/viewPresets/{presetId}
// ===================================

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  increment,
  serverTimestamp,
  type DocumentData
} from 'firebase/firestore'
import { db } from '../../config/firebase'
import type {
  ViewPresetDocument,
  CreatePresetRequest,
  UpdatePresetRequest,
  PresetResponse,
  PresetsQueryFilter
} from '../../interfaces/firestore/fields-management'

export class ViewPresetsRepository {
  private subcollectionName = 'viewPresets'

  /**
   * Obtener referencia a la subcolección de presets de una empresa
   */
  private getCollectionRef(companyId: string) {
    return collection(db, 'Companies', companyId, this.subcollectionName)
  }

  /**
   * Obtener referencia a un documento de preset
   */
  private getDocRef(companyId: string, presetId: string) {
    return doc(db, 'Companies', companyId, this.subcollectionName, presetId)
  }

  /**
   * Crear un nuevo preset
   */
  async createPreset(
    request: CreatePresetRequest,
    userId: string
  ): Promise<PresetResponse> {
    try {
      const companyId = request.companyId
      if (!companyId) {
        return { success: false, error: 'companyId es requerido' }
      }

      // Validar nombre único para la empresa y módulo
      const isUnique = await this.validatePresetName(
        request.name,
        request.module,
        companyId
      )

      if (!isUnique) {
        return {
          success: false,
          error: `Ya existe un preset con el nombre "${request.name}" en este módulo`
        }
      }

      const presetData: Record<string, any> = {
        module: request.module,
        name: request.name,
        fields: request.fields,
        fieldOrder: request.fieldOrder,
        metadata: {
          createdAt: serverTimestamp(),
          createdBy: userId,
          updatedAt: serverTimestamp(),
          isPublic: request.isPublic ?? false,
          isSystem: false,
          usageCount: 0
        }
      }

      // Solo incluir description si tiene valor (Firestore rechaza undefined)
      if (request.description != null) {
        presetData.description = request.description
      }

      if (request.filters != null) {
        presetData.filters = request.filters
      }
      if (request.sort != null) {
        presetData.sort = request.sort
      }
      if (request.assignedUserIds != null) {
        presetData.assignedUserIds = request.assignedUserIds
      }

      const docRef = await addDoc(
        this.getCollectionRef(companyId),
        presetData
      )

      const createdDoc = await getDoc(docRef)
      const preset = this.documentToPreset(createdDoc.id, createdDoc.data()!, companyId)

      return {
        success: true,
        preset
      }
    } catch (error: any) {
      console.error('Error creating preset:', error)
      return {
        success: false,
        error: error.message || 'Error al crear el preset'
      }
    }
  }

  /**
   * Actualizar un preset existente
   */
  async updatePreset(
    presetId: string,
    updates: UpdatePresetRequest,
    userId: string,
    companyId: string,
    options: { isAdmin?: boolean } = {}
  ): Promise<PresetResponse> {
    try {
      const docRef = this.getDocRef(companyId, presetId)

      const presetDoc = await getDoc(docRef)
      if (!presetDoc.exists()) {
        return {
          success: false,
          error: 'Preset no encontrado'
        }
      }

      const presetData = presetDoc.data()

      // No permitir editar presets del sistema
      if (presetData.metadata.isSystem) {
        return {
          success: false,
          error: 'No se pueden editar presets del sistema'
        }
      }

      // Verificar que el usuario es el creador (los admins pueden editar cualquier preset)
      if (presetData.metadata.createdBy !== userId && !options.isAdmin) {
        return {
          success: false,
          error: 'Solo el creador puede editar este preset'
        }
      }

      const updateData: any = {
        'metadata.updatedAt': serverTimestamp(),
        'metadata.updatedBy': userId
      }
      // Omitir claves undefined (Firestore las rechaza); null sí es válido (limpia el campo)
      // isPublic vive dentro de metadata; el resto son campos de primer nivel
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined) return
        updateData[key === 'isPublic' ? 'metadata.isPublic' : key] = value
      })

      await updateDoc(docRef, updateData)

      const updatedDoc = await getDoc(docRef)
      const preset = this.documentToPreset(updatedDoc.id, updatedDoc.data()!, companyId)

      return {
        success: true,
        preset
      }
    } catch (error: any) {
      console.error('Error updating preset:', error)
      return {
        success: false,
        error: error.message || 'Error al actualizar el preset'
      }
    }
  }

  /**
   * Eliminar un preset
   */
  async deletePreset(
    presetId: string,
    userId: string,
    companyId: string,
    options: { isAdmin?: boolean } = {}
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const docRef = this.getDocRef(companyId, presetId)

      const presetDoc = await getDoc(docRef)
      if (!presetDoc.exists()) {
        return {
          success: false,
          error: 'Preset no encontrado'
        }
      }

      const presetData = presetDoc.data()

      // No permitir eliminar presets del sistema
      if (presetData.metadata.isSystem) {
        return {
          success: false,
          error: 'No se pueden eliminar presets del sistema'
        }
      }

      // Verificar que el usuario es el creador (los admins pueden eliminar cualquier preset)
      if (presetData.metadata.createdBy !== userId && !options.isAdmin) {
        return {
          success: false,
          error: 'Solo el creador puede eliminar este preset'
        }
      }

      await deleteDoc(docRef)

      return { success: true }
    } catch (error: any) {
      console.error('Error deleting preset:', error)
      return {
        success: false,
        error: error.message || 'Error al eliminar el preset'
      }
    }
  }

  /**
   * Obtener presets por filtros
   */
  async getPresets(
    filters: PresetsQueryFilter
  ): Promise<ViewPresetDocument[]> {
    try {
      if (!filters.companyId) return []

      // Query base sobre la subcolección de la empresa
      let q = query(
        this.getCollectionRef(filters.companyId),
        where('module', '==', filters.module)
      )

      // Filtrar por público
      if (filters.includePublic !== undefined) {
        q = query(q, where('metadata.isPublic', '==', filters.includePublic))
      }

      // Filtrar por creador
      if (filters.createdBy) {
        q = query(q, where('metadata.createdBy', '==', filters.createdBy))
      }

      // Filtrar por usuario asignado
      if (filters.assignedTo) {
        q = query(q, where('assignedUserIds', 'array-contains', filters.assignedTo))
      }

      const snapshot = await getDocs(q)

      return snapshot.docs
        .map(doc => this.documentToPreset(doc.id, doc.data(), filters.companyId!))
        .sort((a, b) => {
          // Ordenar: Sistema > Públicos > Privados
          if (a.metadata.isSystem && !b.metadata.isSystem) return -1
          if (!a.metadata.isSystem && b.metadata.isSystem) return 1
          if (a.metadata.isPublic && !b.metadata.isPublic) return -1
          if (!a.metadata.isPublic && b.metadata.isPublic) return 1
          return 0
        })
    } catch (error: any) {
      console.error('Error fetching presets:', error)
      return []
    }
  }

  /**
   * Obtener presets del sistema para un módulo
   */
  async getSystemPresets(module: string, companyId: string): Promise<ViewPresetDocument[]> {
    try {
      const q = query(
        this.getCollectionRef(companyId),
        where('module', '==', module),
        where('metadata.isSystem', '==', true)
      )

      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc =>
        this.documentToPreset(doc.id, doc.data(), companyId)
      )
    } catch (error: any) {
      console.error('Error fetching system presets:', error)
      return []
    }
  }

  /**
   * Obtener presets de una empresa
   */
  async getCompanyPresets(
    module: string,
    companyId: string,
    includePrivate: boolean = false
  ): Promise<ViewPresetDocument[]> {
    try {
      let q = query(
        this.getCollectionRef(companyId),
        where('module', '==', module)
      )

      if (!includePrivate) {
        q = query(q, where('metadata.isPublic', '==', true))
      }

      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc =>
        this.documentToPreset(doc.id, doc.data(), companyId)
      )
    } catch (error: any) {
      console.error('Error fetching company presets:', error)
      return []
    }
  }

  /**
   * Obtener presets de un usuario
   */
  async getUserPresets(
    module: string,
    userId: string,
    companyId: string
  ): Promise<ViewPresetDocument[]> {
    try {
      const q = query(
        this.getCollectionRef(companyId),
        where('module', '==', module),
        where('metadata.createdBy', '==', userId)
      )

      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc =>
        this.documentToPreset(doc.id, doc.data(), companyId)
      )
    } catch (error: any) {
      console.error('Error fetching user presets:', error)
      return []
    }
  }

  /**
   * Obtener presets asignados a un usuario (vistas obligatorias)
   */
  async getAssignedPresets(
    module: string,
    userId: string,
    companyId: string
  ): Promise<ViewPresetDocument[]> {
    try {
      const q = query(
        this.getCollectionRef(companyId),
        where('module', '==', module),
        where('assignedUserIds', 'array-contains', userId)
      )

      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc =>
        this.documentToPreset(doc.id, doc.data(), companyId)
      )
    } catch (error: any) {
      console.error('Error fetching assigned presets:', error)
      return []
    }
  }

  /**
   * Obtener preset por ID
   */
  async getPresetById(presetId: string, companyId: string): Promise<ViewPresetDocument | null> {
    try {
      const docRef = this.getDocRef(companyId, presetId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      return this.documentToPreset(docSnap.id, docSnap.data(), companyId)
    } catch (error: any) {
      console.error('Error fetching preset:', error)
      return null
    }
  }

  /**
   * Incrementar contador de uso de preset
   */
  async incrementUsageCount(presetId: string, companyId: string): Promise<void> {
    try {
      const docRef = this.getDocRef(companyId, presetId)
      await updateDoc(docRef, {
        'metadata.usageCount': increment(1)
      })
    } catch (error: any) {
      console.error('Error incrementing usage count:', error)
    }
  }

  /**
   * Validar nombre único de preset
   */
  async validatePresetName(
    name: string,
    module: string,
    companyId: string
  ): Promise<boolean> {
    try {
      const q = query(
        this.getCollectionRef(companyId),
        where('module', '==', module),
        where('name', '==', name)
      )

      const snapshot = await getDocs(q)
      return snapshot.empty
    } catch (error: any) {
      console.error('Error validating preset name:', error)
      return false
    }
  }

  /**
   * Convertir documento de Firestore a ViewPresetDocument
   */
  private documentToPreset(
    id: string,
    data: DocumentData,
    companyId: string
  ): ViewPresetDocument {
    return {
      id,
      companyId,
      module: data.module,
      name: data.name,
      description: data.description,
      fields: data.fields || [],
      fieldOrder: data.fieldOrder || [],
      filters: data.filters,
      sort: data.sort ?? null,
      assignedUserIds: data.assignedUserIds || [],
      metadata: {
        createdAt: data.metadata.createdAt?.toDate() || new Date(),
        createdBy: data.metadata.createdBy,
        updatedAt: data.metadata.updatedAt?.toDate() || new Date(),
        updatedBy: data.metadata.updatedBy,
        isPublic: data.metadata.isPublic,
        isSystem: data.metadata.isSystem,
        usageCount: data.metadata.usageCount || 0
      }
    }
  }

  /**
   * Crear preset del sistema (solo para migración inicial)
   */
  async createSystemPreset(
    companyId: string,
    module: string,
    name: string,
    description: string,
    fields: string[],
    fieldOrder: string[]
  ): Promise<PresetResponse> {
    try {
      const presetData = {
        module,
        name,
        description,
        fields,
        fieldOrder,
        metadata: {
          createdAt: serverTimestamp(),
          createdBy: 'system',
          updatedAt: serverTimestamp(),
          isPublic: true,
          isSystem: true,
          usageCount: 0
        }
      }

      const docRef = await addDoc(
        this.getCollectionRef(companyId),
        presetData
      )

      const createdDoc = await getDoc(docRef)
      const preset = this.documentToPreset(createdDoc.id, createdDoc.data()!, companyId)

      return {
        success: true,
        preset
      }
    } catch (error: any) {
      console.error('Error creating system preset:', error)
      return {
        success: false,
        error: error.message || 'Error al crear preset del sistema'
      }
    }
  }
}

// Exportar instancia singleton
export const viewPresetsRepository = new ViewPresetsRepository()
